import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { loginApi, registerApi } from '@/api/auth';
import { notifyAuthChanged, setToken } from '@/lib/auth-storage';
import { syncSaleSmartlyLoginFromAuthUser } from '@/lib/salesmartly';

type Tab = 'login' | 'register';

export default function AuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(location.pathname === '/register' ? 'register' : 'login');

  const [loginForm, setLoginForm] = useState({ username: '', password: '', remember: true });
  const [registerForm, setRegisterForm] = useState({ username: '', email: '', password: '', confirm: '', agree: false, inviteCode: '' });
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showRegConfirm, setShowRegConfirm] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const invite = searchParams.get('invite');
    if (invite) {
      setTab('register');
      setRegisterForm((f) => ({ ...f, inviteCode: invite.trim() }));
    }
  }, [searchParams]);

  const resolvePostAuthTarget = () => {
    const raw = searchParams.get('redirect');
    let target = '/account';
    if (raw) {
      try {
        const path = decodeURIComponent(raw).split('?')[0];
        if (path.startsWith('/') && !path.startsWith('//')) {
          target = path;
        }
      } catch {
        /* ignore */
      }
    }
    return target;
  };

  const completeLogin = (res: Awaited<ReturnType<typeof loginApi>>) => {
    if (res.code !== 0 || !res.data?.accessToken) {
      return res.msg || '登录失败';
    }
    setToken(res.data.accessToken);
    notifyAuthChanged();
    if (res.data.user) {
      syncSaleSmartlyLoginFromAuthUser(res.data.user);
    }
    navigate(resolvePostAuthTarget());
    return null;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      setLoginError('请填写用户名和密码');
      return;
    }
    setLoginError('');
    setSubmitting(true);
    try {
      const res = await loginApi(loginForm.username.trim(), loginForm.password);
      const err = completeLogin(res);
      if (err) {
        setLoginError(err);
      }
    } catch {
      setLoginError('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerForm.username || !registerForm.email || !registerForm.password) {
      setRegisterError('请填写所有必填项');
      return;
    }
    if (registerForm.password !== registerForm.confirm) {
      setRegisterError('两次输入的密码不一致');
      return;
    }
    if (!registerForm.agree) {
      setRegisterError('请阅读并同意服务条款');
      return;
    }
    setRegisterError('');
    setSubmitting(true);
    try {
      const userName = registerForm.username.trim();
      const password = registerForm.password;
      const res = await registerApi({
        userName,
        email: registerForm.email.trim(),
        password,
        inviteCode: registerForm.inviteCode.trim() || undefined,
        registerDomain: window.location.hostname,
      });
      if (res.code !== 0) {
        setRegisterError(res.msg || '注册失败');
        return;
      }
      const loginRes = await loginApi(userName, password);
      const err = completeLogin(loginRes);
      if (err) {
        setRegisterError(`注册成功，但自动登录失败：${err}，请手动登录`);
        setTab('login');
        setLoginForm((f) => ({ ...f, username: userName, password: '' }));
      }
    } catch {
      setRegisterError('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center space-x-2">
          <img src="/new_logo.png" alt="查191" className="h-12 md:h-14 w-auto object-contain" />
        </Link>
        <Link to="/" className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" />
          </div>
          <span>返回首页</span>
        </Link>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-gray-100 p-8">

            {/* Logo + title */}
            <div className="text-center mb-7">
              <div className="mx-auto mb-4 flex items-center justify-center">
                <img src="/new_logo.png" alt="查191" className="h-[72px] md:h-20 w-auto object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {tab === 'login' ? '登录账户' : '创建账户'}
              </h1>
              <p className="text-sm text-gray-400 mt-1">
                {tab === 'login' ? '欢迎回来，请登录您的账户' : '注册成为全网查询会员'}
              </p>
            </div>

            {/* Tab switcher */}
            <div className="flex bg-gray-100 rounded-xl p-1 mb-7">
              <button
                onClick={() => { setTab('login'); setLoginError(''); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  tab === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                登录
              </button>
              <button
                onClick={() => { setTab('register'); setRegisterError(''); setRegisterSuccess(false); }}
                className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  tab === 'register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                注册
              </button>
            </div>

            {/* Login form */}
            {tab === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">用户名或邮箱</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                      placeholder="请输入用户名或邮箱"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">密码</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-lock-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type={showLoginPwd ? 'text' : 'password'}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      placeholder="请输入密码"
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPwd(!showLoginPwd)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${showLoginPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-sm`} />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={loginForm.remember}
                      onChange={(e) => setLoginForm({ ...loginForm, remember: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 accent-gray-900 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">记住我</span>
                  </label>
                  <button type="button" className="text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                    忘记密码？
                  </button>
                </div>

                {loginError && (
                  <div className="flex items-center space-x-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line" />
                    </div>
                    <span>{loginError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '登录中…' : '登录'}
                </button>

                <p className="text-center text-sm text-gray-400">
                  还没有账号？
                  <button
                    type="button"
                    onClick={() => setTab('register')}
                    className="text-gray-700 font-medium hover:text-gray-900 transition-colors cursor-pointer ml-1"
                  >
                    立即注册
                  </button>
                </p>
              </form>
            )}

            {/* Register form */}
            {tab === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">用户名 <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={registerForm.username}
                      onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                      placeholder="请设置用户名（4-16位字符）"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">邮箱 <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-mail-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      placeholder="请输入邮箱地址"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">密码 <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-lock-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type={showRegPwd ? 'text' : 'password'}
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      placeholder="请设置密码（至少8位）"
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                    <button type="button" onClick={() => setShowRegPwd(!showRegPwd)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${showRegPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-sm`} />
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">确认密码 <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-lock-2-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type={showRegConfirm ? 'text' : 'password'}
                      value={registerForm.confirm}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirm: e.target.value })}
                      placeholder="请再次输入密码"
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                    <button type="button" onClick={() => setShowRegConfirm(!showRegConfirm)} className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${showRegConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 text-sm`} />
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">邀请码 <span className="text-gray-400 font-normal">（选填）</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-gift-line text-gray-400 text-sm" />
                      </div>
                    </div>
                    <input
                      type="text"
                      value={registerForm.inviteCode}
                      onChange={(e) => setRegisterForm({ ...registerForm, inviteCode: e.target.value })}
                      placeholder="请输入邀请码（可获得额外奖励）"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <label className="flex items-start space-x-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={registerForm.agree}
                    onChange={(e) => setRegisterForm({ ...registerForm, agree: e.target.checked })}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-gray-900 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm text-gray-500 leading-relaxed">
                    我已阅读并同意
                    <button type="button" className="text-gray-700 font-medium hover:underline cursor-pointer mx-0.5">服务条款</button>
                    和
                    <button type="button" className="text-gray-700 font-medium hover:underline cursor-pointer mx-0.5">隐私政策</button>
                  </span>
                </label>

                {registerError && (
                  <div className="flex items-center space-x-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line" />
                    </div>
                    <span>{registerError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? '提交中…' : '立即注册'}
                </button>

                <p className="text-center text-sm text-gray-400">
                  已有账号？
                  <button
                    type="button"
                    onClick={() => setTab('login')}
                    className="text-gray-700 font-medium hover:text-gray-900 transition-colors cursor-pointer ml-1"
                  >
                    立即登录
                  </button>
                </p>
              </form>
            )}

          </div>

          {/* Bottom note */}
          <p className="text-center text-xs text-gray-400 mt-5">
            注册即表示您同意我们的服务条款和隐私政策
          </p>
        </div>
      </div>
    </div>
  );
}
